import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { SearchInput } from '@/components/SearchInput';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { roleActionErrorToast, supportTicketCloseSuccessToast } from '@/lib/user-feedback';
import {
  CATEGORY_LABELS,
  type SupportTicket,
  type SupportTicketCategory,
  type SupportTicketStatus,
} from './types';

const PER_PAGE = 20;

type QuickTab = 'abierto' | 'en_progreso' | 'todos';

function userInitials(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);

  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} ${days === 1 ? 'día' : 'días'}`;

  return date.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
}

function tabForStatus(status: SupportTicketStatus | ''): QuickTab {
  if (status === 'abierto') return 'abierto';
  if (status === 'en_progreso') return 'en_progreso';
  return 'todos';
}

export function SoporteAdminPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canAdminister = hasPermission(PERMISSIONS.SOPORTE_ADMINISTRAR);
  const [ticketToClose, setTicketToClose] = useState<SupportTicket | null>(null);

  const [status, setStatus] = useState<SupportTicketStatus | ''>('abierto');
  const [category, setCategory] = useState<SupportTicketCategory | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const appliedSearch = useDebouncedValue(search, 300).trim();

  const activeTab = tabForStatus(status);

  const queryParams = new URLSearchParams();
  if (status) queryParams.set('status', status);
  if (category) queryParams.set('category', category);
  if (appliedSearch) queryParams.set('search', appliedSearch);
  queryParams.set('page', String(page));
  queryParams.set('perPage', String(PER_PAGE));

  const { data, isLoading } = useQuery({
    queryKey: ['support', 'admin', 'tickets', status, category, appliedSearch, page],
    queryFn: () =>
      api<{ data: SupportTicket[]; meta: { total: number; page: number; lastPage: number; perPage: number } }>(
        `/support/tickets?${queryParams.toString()}`,
      ),
  });

  const closeMutation = useMutation({
    mutationFn: (ticketId: string) =>
      api<SupportTicket>(`/support/tickets/${ticketId}`, { method: 'PATCH', body: { status: 'cerrado' } }),
    onSuccess: async () => {
      setTicketToClose(null);
      toast(supportTicketCloseSuccessToast());
      await queryClient.invalidateQueries({ queryKey: ['support', 'admin', 'tickets'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo cerrar el ticket'));
    },
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const rangeStart = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const rangeEnd = Math.min(page * PER_PAGE, total);

  useEffect(() => {
    setPage(1);
  }, [appliedSearch]);

  function selectTab(tab: QuickTab) {
    setStatus(tab === 'todos' ? '' : tab);
    setPage(1);
  }

  function handleClose(ticket: SupportTicket) {
    setTicketToClose(ticket);
  }

  return (
    <div className="lms-support-admin lms-site-admin-hub-page font-sans">
      <ConfirmModal
        open={ticketToClose !== null}
        title="Cerrar ticket"
        description={
          ticketToClose
            ? `¿Cerrar el ticket #${ticketToClose.number} «${ticketToClose.subject}»?`
            : undefined
        }
        confirmLabel="Cerrar"
        loading={closeMutation.isPending}
        onConfirm={() => {
          if (ticketToClose) closeMutation.mutate(ticketToClose.id);
        }}
        onClose={() => {
          if (!closeMutation.isPending) setTicketToClose(null);
        }}
      />
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-2 font-sans">
          <h1 className="dash-serif text-3xl font-bold text-dash-primary md:text-4xl">Mesa de Ayuda</h1>
          <p className="text-sm text-dash-on-surface-variant md:text-base">
            Bandeja de tickets de soporte de estudiantes.
          </p>
        </header>

        <div className="support-admin-tabs" role="tablist" aria-label="Filtros rápidos">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'abierto'}
            className={`support-admin-tab ${activeTab === 'abierto' ? 'support-admin-tab--active' : ''}`}
            onClick={() => selectTab('abierto')}
          >
            Abiertos
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'en_progreso'}
            className={`support-admin-tab ${activeTab === 'en_progreso' ? 'support-admin-tab--active' : ''}`}
            onClick={() => selectTab('en_progreso')}
          >
            En progreso
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'todos'}
            className={`support-admin-tab ${activeTab === 'todos' ? 'support-admin-tab--active' : ''}`}
            onClick={() => selectTab('todos')}
          >
            Todos
          </button>
        </div>

        <form onSubmit={(event) => event.preventDefault()} className="support-admin-filters">
          <div className="support-admin-filters__grid">
            <div className="support-admin-field support-admin-field--category">
              <label htmlFor="support-filter-category">Categoría</label>
              <div className="support-admin-select-wrap">
                <select
                  id="support-filter-category"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value as SupportTicketCategory | '');
                    setPage(1);
                  }}
                  className="support-admin-select"
                >
                  <option value="">Todas</option>
                  {(Object.keys(CATEGORY_LABELS) as SupportTicketCategory[]).map((key) => (
                    <option key={key} value={key}>
                      {CATEGORY_LABELS[key]}
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined" aria-hidden="true">
                  expand_more
                </span>
              </div>
            </div>

            <div className="support-admin-field support-admin-field--search">
              <label htmlFor="support-filter-search">Búsqueda</label>
              <SearchInput
                id="support-filter-search"
                value={search}
                onChange={setSearch}
                placeholder="Buscar por alumno o asunto..."
                aria-label="Buscar por alumno o asunto"
                className="support-admin-input-wrap"
              />
            </div>
          </div>

        </form>

        <section className="lms-gradebook lms-gradebook--forum">
          <div className="lms-course-forum-card">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader label="Cargando tickets…" />
              </div>
            ) : rows.length === 0 ? (
              <div className="lms-gradebook__table-wrap">
                <div className="lms-gradebook__empty">
                  <div className="lms-gradebook__empty-icon" aria-hidden="true">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      support_agent
                    </span>
                  </div>
                  <h3>Sin tickets</h3>
                  <p>No hay tickets que coincidan con los filtros.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th className="lms-gradebook__th" scope="col">
                          #
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Alumno
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Asunto
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Categoría
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Estado
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Prioridad
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Actualizado
                        </th>
                        <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const studentName = row.user.name || row.user.email;
                        return (
                          <tr key={row.id}>
                            <td className="lms-gradebook__td">{row.number}</td>
                            <th className="lms-gradebook__td" scope="row">
                              <div className="lms-gradebook__student">
                                <div className="lms-gradebook__avatar" aria-hidden="true">
                                  {userInitials(row.user.name, row.user.email) || '?'}
                                </div>
                                <Link
                                  to={`/soporte/admin/${row.id}`}
                                  className="lms-gradebook__name"
                                  title={row.user.email}
                                >
                                  {studentName}
                                </Link>
                              </div>
                            </th>
                            <td className="lms-gradebook__td">
                              <span className="lms-gradebook__muted">{row.subject}</span>
                            </td>
                            <td className="lms-gradebook__td">
                              <Badge variant="secondary">{row.categoryLabel}</Badge>
                            </td>
                            <td className="lms-gradebook__td">
                              <Badge
                                variant={
                                  row.status === 'abierto'
                                    ? 'danger'
                                    : row.status === 'en_progreso'
                                      ? 'primary'
                                      : row.status === 'resuelto'
                                        ? 'success'
                                        : 'default'
                                }
                              >
                                {row.statusLabel}
                              </Badge>
                            </td>
                            <td className="lms-gradebook__td">
                              {row.priority === 'alta' ? (
                                <Badge variant="danger">{row.priorityLabel}</Badge>
                              ) : (
                                <span className="lms-gradebook__muted">{row.priorityLabel}</span>
                              )}
                            </td>
                            <td className="lms-gradebook__td">
                              <span className="lms-gradebook__muted">{formatRelative(row.updatedAt)}</span>
                            </td>
                            <td className="lms-gradebook__td lms-gradebook__td--actions">
                              <div className="flex items-center justify-end gap-1">
                                <Link
                                  to={`/soporte/admin/${row.id}`}
                                  className="lms-gradebook__action"
                                  title="Ver"
                                  aria-label={`Ver ticket ${row.number}`}
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    visibility
                                  </span>
                                </Link>
                                <button
                                  type="button"
                                  className="lms-gradebook__action"
                                  title="Cerrar"
                                  aria-label={`Cerrar ticket ${row.number}`}
                                  disabled={!canAdminister || row.status === 'cerrado' || closeMutation.isPending}
                                  onClick={() => handleClose(row)}
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    check
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="lms-course-forum-card__footer">
                  <span className="lms-course-forum-card__meta">
                    Mostrando <strong>{rangeStart}</strong> a <strong>{rangeEnd}</strong> de <strong>{total}</strong>{' '}
                    resultados
                  </span>
                  <div className="lms-course-forum-card__pager">
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página anterior"
                      disabled={page <= 1}
                      onClick={() => setPage((current) => current - 1)}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_left
                      </span>
                    </button>
                    <span className="lms-course-forum-card__pager-current">{page}</span>
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página siguiente"
                      disabled={!meta || page >= meta.lastPage}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_right
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
